import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Moment from "moment";
import Icon from "components/blocks/Icon/Icon";
import NoData from "components/pages/noData/NoData";
import "./ManagementItemBlock.scss";

type GestionTab = "administracion" | "economico" ; //| "juridico";

interface ManagementComponent {
  id: string;
  nombre: string | null;
  fecha: string | null;
  empresa: string | null;
  tipo_componente: number;
}

interface ManagementGroup {
  ComponenteNombre: string;
  Count: number;
  Componentes: ManagementComponent[];
}

interface LegacyManagementItem {
  id: string;
  nombre: string;
  fecha?: string;
  empresa?: string;
  tipo_componente: number;
}

interface Props {
  administrativeData: ManagementGroup[] | LegacyManagementItem[];
  economicData: ManagementGroup[] | LegacyManagementItem[];
  //juridicData?: ManagementGroup[] | LegacyManagementItem[];
}

const TABS: { key: GestionTab; label: string; icon: string }[] = [
  { key: "administracion", label: "Administración", icon: "calendar" },
  { key: "economico",      label: "Económico",      icon: "euro"     },
  // { key: "juridico",       label: "Jurídico",       icon: "shield"   },
];

interface CardItem {
  item: ManagementGroup;
  representative?: ManagementComponent;
  isDisabled?: boolean;
  totalCount: number;
  cardClass: string;
  iconName: string;
  iconClass?: string;
  onView: () => void;
}

const MgmtCard: React.FC<CardItem> = ({
  item,
  representative,
  isDisabled = false,
  totalCount,
  cardClass,
  iconName,
  iconClass,
  onView,
}) => {
  const displayName = item.ComponenteNombre;
  const displayCompany = representative?.empresa || "-";
  const displayDate = representative?.fecha && representative.fecha !== "0001-01-01T00:00:00"
    ? Moment(representative.fecha).format("DD/MM/YYYY")
    : "-";

  return (
  <div className={`mgmt-card ${cardClass}${isDisabled ? " mgmt-card--disabled" : ""}`}>
    <span className="mgmt-card__count">{totalCount}</span>
    <span className={`mgmt-card__icon${iconClass ? ` ${iconClass}` : ""}`}>
      <Icon name={iconName} size={16} />
    </span>
    <div className="mgmt-card__content">
      <div className="mgmt-card__name">{displayName}</div>
      {!isDisabled && (
        <>
          <div className="mgmt-card__empresa">{displayCompany}</div>
          <div className="mgmt-card__date">{displayDate}</div>
        </>
      )}
    </div>
    <div className="mgmt-card__actions">
      <button className="mgmt-card__view-btn" onClick={onView} type="button" disabled={isDisabled}>
        <Icon name="eye" size={14} />
        Ver
      </button>
    </div>
  </div>
);
};

const isGroupedData = (
  data: ManagementGroup[] | LegacyManagementItem[]
): data is ManagementGroup[] => {
  return Array.isArray(data) && data.length > 0 && Array.isArray((data[0] as ManagementGroup).Componentes);
};

const normalizeToGroups = (
  data: ManagementGroup[] | LegacyManagementItem[]
): ManagementGroup[] => {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (isGroupedData(data)) return data;

  return (data as LegacyManagementItem[]).map((item) => ({
    ComponenteNombre: item.nombre,
    Count: 1,
    Componentes: [
      {
        id: item.id,
        nombre: item.nombre,
        fecha: item.fecha ?? null,
        empresa: item.empresa ?? null,
        tipo_componente: item.tipo_componente,
      },
    ],
  }));
};

const getGroupCount = (group: ManagementGroup): number => {
  if (typeof group.Count === "number" && group.Count > 0) {
    return group.Count;
  }

  return Array.isArray(group.Componentes) ? group.Componentes.length : 0;
};

const isGroupActive = (group: ManagementGroup): boolean => {
  const totalCount = getGroupCount(group);
  const representative = group.Componentes[0];
  return totalCount > 0 && Boolean(representative);
};

const GestionTabs: React.FC<Props> = ({
  administrativeData,
  economicData,
  //juridicData = [],
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();

  const administrativeGroups = normalizeToGroups(administrativeData);
  const economicGroups = normalizeToGroups(economicData);
  //const juridicGroups = normalizeToGroups(juridicData);

  // Contar solo grupos activos para los contadores, pero mostrar todos los tabs
  const administrativeActiveCount = administrativeGroups.filter(isGroupActive).length;
  const economicActiveCount = economicGroups.filter(isGroupActive).length;
  //const juridicActiveCount = juridicGroups.filter(isGroupActive).length;

  const counts: Record<GestionTab, number> = {
    administracion: administrativeActiveCount,
    economico:      economicActiveCount,
    // juridico:       juridicActiveCount,
  };

  const [activeTab, setActiveTab] = useState<GestionTab>("administracion");

  React.useEffect(() => {
    // Cambiar de tab solo si el tab actual no tiene grupos (está completamente vacío)
    // Nota: juridico está desactivado, no se puede cambiar a ese tab
    if (administrativeGroups.length === 0 && activeTab === "administracion") {
      if (economicGroups.length > 0) {
        setActiveTab("economico");
      }
    } else if (economicGroups.length === 0 && activeTab === "economico") {
      if (administrativeGroups.length > 0) {
        setActiveTab("administracion");
      }
    }
  }, [administrativeGroups.length, economicGroups.length]);

  const getGroupRepresentative = (group: ManagementGroup): ManagementComponent | undefined => {
    return group.Componentes[0];
  };

  const renderContent = () => {
    switch (activeTab) {
      case "administracion":
        return (
          <>
            <p className="mgmt-tabs__desc">
              Información referente a hechos de gestión no considerados asistenciales y económicos.
            </p>
            {administrativeGroups.length === 0 ? (
              <div className="mgmt-empty">
                <Icon name="file-text" size={18} />
                <span>No hay componentes administrativos activos en este momento.</span>
              </div>
            ) : (
              <div className="mgmt-items">
                {administrativeGroups.map((group) => {
                  const representative = getGroupRepresentative(group);
                  const totalCount = getGroupCount(group);
                  const isDisabled = totalCount <= 0 || !representative;

                  return (
                    <MgmtCard
                      key={`${group.ComponenteNombre}-${representative?.id ?? "empty"}`}
                      item={group}
                      representative={representative}
                      isDisabled={isDisabled}
                      totalCount={totalCount}
                      cardClass=""
                      iconName="calendar"
                      onView={() => {
                        if (isDisabled || !representative) return;
                        navigate(`/historial/${params.id}/administración/grupo/${representative.tipo_componente}`, {
                          state: {
                            groupName: group.ComponenteNombre,
                            components: group.Componentes.filter(
                              (component) => Number(component.tipo_componente) === Number(representative.tipo_componente)
                            ),
                            iconName: "calendar",
                            variant: "blue",
                          },
                        });
                      }}
                    />
                  );
                })}
              </div>
            )}
          </>
        );

      case "economico":
        return (
          <>
            <p className="mgmt-tabs__desc">
              Información económica referente a subsidios, transferencias bancarias, cotizaciones, entre otras.
            </p>
            {economicGroups.length === 0 ? (
              <div className="mgmt-empty">
                <Icon name="euro" size={18} />
                <span>No hay componentes económicos activos en este momento.</span>
              </div>
            ) : (
              <div className="mgmt-items">
                {economicGroups.map((group) => {
                  const representative = getGroupRepresentative(group);
                  const totalCount = getGroupCount(group);
                  const isDisabled = totalCount <= 0 || !representative;

                  return (
                    <MgmtCard
                      key={`${group.ComponenteNombre}-${representative?.id ?? "empty"}`}
                      item={group}
                      representative={representative}
                      isDisabled={isDisabled}
                      totalCount={totalCount}
                      cardClass="mgmt-card--amber"
                      iconName="euro"
                      iconClass="mgmt-card__icon--amber"
                      onView={() => {
                        if (isDisabled || !representative) return;
                        navigate(`/historial/${params.id}/económico/grupo/${representative.tipo_componente}`, {
                          state: {
                            groupName: group.ComponenteNombre,
                            components: group.Componentes.filter(
                              (component) => Number(component.tipo_componente) === Number(representative.tipo_componente)
                            ),
                            iconName: "euro",
                            variant: "amber",
                          },
                        });
                      }}
                    />
                  );
                })}
              </div>
            )}
          </>
        );

      // case "juridico":
      //   return (
      //     <>
      //       <p className="mgmt-tabs__desc">
      //         Información jurídica referente a resoluciones, recursos, reclamaciones y procedimientos legales asociados a tu prestación.
      //       </p>
      //       {juridicGroups.length === 0 ? (
      //         <div className="mgmt-empty">
      //           <Icon name="shield" size={18} />
      //           <span>No hay componentes jurídicos activos en este momento.</span>
      //         </div>
      //       ) : (
      //         <div className="mgmt-items">
      //           {juridicGroups.map((group) => {
      //             const representative = getGroupRepresentative(group);
      //             const totalCount = getGroupCount(group);
      //             const isDisabled = totalCount <= 0 || !representative;

      //             return (
      //               <MgmtCard
      //                 key={`${group.ComponenteNombre}-${representative?.id ?? "empty"}`}
      //                 item={group}
      //                 representative={representative}
      //                 isDisabled={isDisabled}
      //                 totalCount={totalCount}
      //                 cardClass="mgmt-card--green"
      //                 iconName="shield"
      //                 iconClass="mgmt-card__icon--green"
      //                 onView={() => {
      //                   if (isDisabled || !representative) return;
      //                   navigate(`/historial/${params.id}/juridico/grupo/${representative.tipo_componente}`, {
      //                     state: {
      //                       groupName: group.ComponenteNombre,
      //                       components: group.Componentes.filter(
      //                         (component) => Number(component.tipo_componente) === Number(representative.tipo_componente)
      //                       ),
      //                       iconName: "shield",
      //                       variant: "green",
      //                     },
      //                   });
      //                 }}
      //               />
      //             );
      //           })}
      //         </div>
      //       )}
      //     </>
      //   );
    }
  };

  // Mostrar los tabs siempre, incluso si están vacíos, para poder ver componentes desactivados
  const hasAnyTab = true;

  if (!hasAnyTab) {
    return <NoData message="No hay gestiones registradas." />;
  }

  return (
    <div className="mgmt-tabs-card">
      <div className="mgmt-tabs__bar" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`mgmt-tabs__tab${activeTab === tab.key ? " is-active" : ""}`}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            type="button"
            //disabled={tab.key === "juridico"}
          >
            <Icon name={tab.icon} size={15} />
            {tab.label}
            {counts[tab.key] > 0 && <span className="mgmt-tabs__count">{counts[tab.key]}</span>}
          </button>
        ))}
      </div>

      <div className="mgmt-tabs__body">
        {renderContent()}
      </div>
    </div>
  );
};

export default GestionTabs;
